import { dublinCivilDate, isBeforeDublinToday } from './dublin-date.js';

/**
 * ⭐⭐ Board 2647 (K2) - WHAT A PORTAL REQUEST LEADS WITH, IN ONE PLACE.
 *
 * Board 2637 (Alex's ruling 1, 2026-09-30) made Helen's Requests row and the
 * booking a request crosses into lead with the COLLECTION. The rule was written
 * twice, once in the browser (`PortalRequests.tsx`) and once in the crossing
 * (`crossingLead` in `server/app.ts`), with two different validity checks on the
 * pickup. This is the one copy both call:
 *   · `appointment` with a pickup that parses -> `{ kind: 'pickup', at }`;
 *   · `collection`                           -> `{ kind: 'own' }`, the
 *     request's own time, instant or pattern;
 *   · anything else                          -> `{ kind: 'none' }`: an
 *     appointment whose pickup is absent, blank or does not parse, and a row
 *     whose meaning was never stated. The crossing writes NO time for it and the
 *     row keeps "(appointment time)"; the appointment is never used as the
 *     collection.
 *
 * ⛔ TYPED BY SHAPE, NOT BY `PortalRequest` OR `PortalRequestSummary`: the
 * client's type and the server's are separate declarations of the same seam, and
 * `src/shared` is imported by both bundles, so it may depend on neither.
 * ⛔ `typeof r.pickupAt === 'string'`, NOT `== null`: a fake or an older client can omit
 * the key or send a non-string, and either must fall to `none`, never to a crash
 * or to the appointment.
 */
export type PortalLead = { kind: 'pickup'; at: Date } | { kind: 'own' } | { kind: 'none' };

export function portalRequestLead(r: {
  timeMeaning: string | null | undefined;
  pickupAt?: string | null | undefined;
}): PortalLead {
  if (r.timeMeaning === 'appointment') {
    if (typeof r.pickupAt !== 'string' || r.pickupAt.trim() === '') return { kind: 'none' };
    const at = new Date(r.pickupAt);
    return Number.isNaN(at.getTime()) ? { kind: 'none' } : { kind: 'pickup', at };
  }
  return r.timeMeaning === 'collection' ? { kind: 'own' } : { kind: 'none' };
}

type DatedRequest = {
  timeMeaning: string | null | undefined;
  pickupAt?: string | null | undefined;
  journeyDate: string | null | undefined;
};

/**
 * ⭐ Board 2814 M2 fix round (code-review Low) - THE INSTANT THE BOOKING IS MADE
 * ON, the one source both readers below derive from: the pickup when the lead rule
 * picks it, else the request's own `journeyDate`, else null (no usable date).
 */
function requestLeadInstant(r: DatedRequest): Date | null {
  const lead = portalRequestLead(r);
  if (lead.kind === 'pickup') return lead.at;
  if (typeof r.journeyDate !== 'string' || r.journeyDate.trim() === '') return null;
  const d = new Date(r.journeyDate);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * ⭐⭐ Board 2814 M2 (the M1 wave's output-gate risk and code-review Low 3) - THE
 * REQUEST'S DUBLIN DAY, IN ONE PLACE: the day its booking is made on. The lead
 * rule above picks the instant (the pickup on an appointment row that has one,
 * else the request's own `journeyDate`), and the day is that instant's Dublin
 * civil date. null for a request with no usable date (a repeating one).
 * ⛔ ONE DAY SOURCE FOR THREE READERS: the crossing's booking day
 * (`crossingJourneyDate` in `server/app.ts`), its `crossing_date_passed` refusal,
 * and the Requests screen's place for the row (`needsHelenNow`). Before M2 the
 * refusal and the screen read `journeyDate` while the booking read the pickup,
 * so an early pickup on the evening before an appointment could book a run on a
 * day that had already passed.
 */
export function portalRequestDay(r: DatedRequest): string | null {
  const at = requestLeadInstant(r);
  return at === null ? null : dublinCivilDate(at);
}

/**
 * ⭐ Board 2814 M2 - HAS THE REQUEST'S DUBLIN DAY (`portalRequestDay`) PASSED AT
 * `now`? The one "past" rule for a nursing request (`isBeforeDublinToday` on the
 * same lead instant). False for a request with no date, so a missing date never
 * refuses a booking or files a row as done.
 */
export function isPortalRequestPast(r: DatedRequest, now: Date): boolean {
  const at = requestLeadInstant(r);
  return at !== null && isBeforeDublinToday(at.toISOString(), now);
}
